import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MenuIcon, UserIcon, LogOutIcon, LanguagesIcon, SunIcon, MoonIcon } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import BusinessHoursBadge from "@/components/BusinessHoursBadge";
import NotificationBell from "@/components/NotificationBell";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser, clearAuth } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import type { Locale } from "@/contexts/LanguageContext";

interface NavItem {
  name: string;
  href: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

interface CompanyBranding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  name: string;
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { locale, setLocale, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [companyBranding, setCompanyBranding] = useState<CompanyBranding | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.title = t.pageTitle.default;
  }, [locale, t]);

  useEffect(() => {
    const checkAuth = () => {
      const userData = getUser();
      const token = getToken();
      
      if (userData && token) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsAuthenticated(true);
        } catch (error) {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
        setCompanyBranding(null);
      }
    };

    checkAuth();
    const interval = setInterval(checkAuth, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchProfileAvatar = () => {
    if (!user) return;
    const tok = getToken();
    if (!tok) return;
    fetch(getApiEndpoint("/api/profile"), {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.user?.avatar_url) setAvatarUrl(data.user.avatar_url);
        else setAvatarUrl(null);
      })
      .catch(() => setAvatarUrl(null));
  };

  // Buscar avatar do perfil quando usuário logado
  useEffect(() => {
    if (user) {
      fetchProfileAvatar();
    } else {
      setAvatarUrl(null);
    }
  }, [user?.id]);

  // Atualizar avatar quando perfil for alterado (ex.: upload na página Editar perfil)
  useEffect(() => {
    const onUserChanged = () => fetchProfileAvatar();
    window.addEventListener("userChanged", onUserChanged);
    return () => window.removeEventListener("userChanged", onUserChanged);
  }, [user?.id]);

  // Buscar branding da empresa quando usuário logar
  useEffect(() => {
    if (user && user.company_id) {
      fetch(getApiEndpoint(`/api/companies/${user.company_id}`), {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data && data.company) {
            setCompanyBranding({
              logo_url: data.company.logo_url,
              primary_color: data.company.primary_color,
              secondary_color: data.company.secondary_color,
              name: data.company.name,
            });
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar branding:", error);
        });
    } else {
      setCompanyBranding(null);
    }
  }, [user]);

  const handleLogout = () => {
    // Revoga o token no servidor antes de esquece-lo aqui. Limpar so o
    // localStorage nao encerrava a sessao: quem tivesse copiado o token seguia
    // entrando com ele ate o fim das 24h de validade.
    const token = getToken();
    if (token) {
      fetch(getApiEndpoint("/api/auth/logout"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => {
        // Sem rede o logout local continua valendo; o token expira sozinho.
      });
    }

    clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    setCompanyBranding(null);
    
    // Resetar cores CSS para padrão
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', '#2563eb');
    root.style.setProperty('--brand-secondary', '#4f46e5');
    root.style.setProperty('--brand-primary-dark', '#1e40af');
    root.style.setProperty('--brand-primary-light', '#eff6ff');
    
    document.title = t.pageTitle.default;
    
    // Disparar evento para atualizar branding
    window.dispatchEvent(new Event("userChanged"));
    
    toast({
      title: t.common.logoutSuccess,
      description: t.common.logoutDescription,
    });
    
    // Recarregar página para limpar todos os estados
    setTimeout(() => {
      navigate("/");
      window.location.reload();
    }, 500);
  };

  /**
   * A navegacao vive agrupada porque um usuario interno chega a 13 destinos, o que
   * nao cabe em uma barra horizontal. Os grupos alimentam o painel lateral; a barra
   * do topo mostra apenas os atalhos primarios.
   */
  const getNavGroups = (): NavGroup[] => {
    const isInternalUser = user && (user.role === "internal" || user.role === "analyst" || user.role === "admin");
    const isStaff = user && (user.role === "admin" || user.role === "internal");

    const serviceItems: NavItem[] = [{ name: t.nav.home, href: "/" }];

    if (!isInternalUser) {
      serviceItems.push(
        { name: t.nav.tickets, href: "/chamados" },
        { name: t.nav.newTicket, href: "/novo-chamado" }
      );
    } else {
      serviceItems.push({ name: t.nav.service, href: "/atendimento" });
    }

    if (isStaff) {
      serviceItems.push({ name: t.nav.queues, href: "/filas" });
    }

    const groups: NavGroup[] = [{ title: t.nav.groupService, items: serviceItems }];

    if (isStaff) {
      groups.push({
        title: t.nav.groupManagement,
        items: [
          { name: t.nav.changes, href: "/mudancas" },
          { name: t.nav.approvals, href: "/aprovacoes" },
          { name: t.nav.problems, href: "/problemas" },
          { name: t.nav.admin, href: "/admin" },
          { name: t.nav.reports, href: "/relatorios" },
        ],
      });
    }

    groups.push({
      title: t.nav.groupKnowledge,
      items: [
        { name: t.nav.sla, href: "/sla" },
        { name: t.nav.faq, href: "/faq" },
        { name: t.nav.policy, href: "/politica-de-servico" },
        { name: t.nav.catalog, href: "/catalogo" },
        { name: t.nav.docs, href: "/documentacao" },
      ],
    });

    return groups;
  };

  const navGroups = getNavGroups();
  // Atalhos que continuam na barra: os primeiros do grupo de atendimento, que sao
  // os destinos do dia a dia. O resto fica a um clique no painel lateral.
  const primaryNavItems = navGroups[0].items.slice(0, 3);

  const isActiveRoute = (href: string) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  const brandPrimary = companyBranding?.primary_color || "#2563eb";
  const brandSecondary = companyBranding?.secondary_color || brandPrimary;
  const logoSrc = companyBranding?.logo_url
    ? (companyBranding.logo_url.startsWith("http")
        ? companyBranding.logo_url
        : getApiEndpoint(companyBranding.logo_url))
    : null;
  const trialInfo = user?.trial;
  const trialDaysRemaining = typeof trialInfo?.days_remaining === "number" ? trialInfo.days_remaining : null;
  const showTrialLabel = trialDaysRemaining !== null;
  const isTrialActive = !!trialInfo?.is_active;
  const trialLabel = trialDaysRemaining !== null
    ? (isTrialActive ? t.trial.label(trialDaysRemaining) : t.trial.labelEnded)
    : null;

  return (
    <header className="sticky top-0 z-50">
      <div
        className="h-1 w-full"
        style={{ background: `linear-gradient(90deg, ${brandPrimary}, ${brandSecondary})` }}
      />
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-md border-b border-gray-200/50 dark:border-gray-800/50">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-20 gap-2 xl:gap-4 min-w-0 overflow-hidden">
          <Link to="/" className="flex items-center space-x-2 xl:space-x-3 group flex-shrink-0">
            {logoSrc ? (
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
                <img 
                  src={logoSrc} 
                  alt={companyBranding.name}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    // Fallback para inicial se imagem falhar
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="relative w-12 h-12 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-105 ring-1 ring-gray-200/50 dark:ring-gray-600/50">
                <img
                  src="/login-assets/tec-it-mascot.jpeg"
                  alt="TEC-IT"
                  className="w-full h-full object-cover object-top scale-110"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
                <div
                  className="hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-600"
                  style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}
                >
                  <span className="text-white font-bold text-xl">
                    {companyBranding?.name?.charAt(0) || "T"}
                  </span>
                </div>
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                {companyBranding?.name || 'TEC-IT'}
              </span>
              <span
                className="text-sm font-semibold bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(90deg, ${brandPrimary}, ${brandSecondary})` }}
              >
                ITSM
              </span>
            </div>
          </Link>

          {/* Atalhos primarios. O menu completo fica no painel lateral. */}
          <nav className="hidden lg:flex flex-1 items-center justify-center min-w-0">
            <div className="flex items-center gap-4 xl:gap-6 min-w-0">
            {primaryNavItems.map((item) => {
              const active = isActiveRoute(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={active ? "page" : undefined}
                  className={`relative px-2 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 group whitespace-nowrap flex-shrink-0 ${
                    active
                      ? "text-gray-900 dark:text-gray-100"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  <span className="relative z-10">{item.name}</span>
                  <span
                    className={`absolute inset-x-0 bottom-0 h-0.5 transform transition-transform duration-200 ${
                      active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                    style={{ background: `linear-gradient(90deg, ${brandPrimary}, ${brandSecondary})` }}
                  />
                </Link>
              );
            })}
            </div>
          </nav>

          <div className="flex items-center gap-2 xl:gap-4 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 pl-3 xl:pl-4">
            {/* Seletor de tema */}
            <Select value={theme} onValueChange={(v) => setTheme(v as "light" | "dark")}>
              <SelectTrigger
                className="w-[100px] xl:w-[120px] h-9 border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80 text-sm shrink-0"
                aria-label={t.theme.label}
              >
                {theme === "dark" ? (
                  <MoonIcon className="h-4 w-4 mr-1.5 text-gray-500 dark:text-gray-400 shrink-0" />
                ) : (
                  <SunIcon className="h-4 w-4 mr-1.5 text-gray-500 shrink-0" />
                )}
                <SelectValue placeholder={t.theme.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">{t.theme.light}</SelectItem>
                <SelectItem value="dark">{t.theme.dark}</SelectItem>
              </SelectContent>
            </Select>
            {/* Seletor de idioma */}
            <Select value={locale} onValueChange={(v) => setLocale(v as Locale)}>
              <SelectTrigger
                className="w-[110px] xl:w-[130px] h-9 border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80 text-sm shrink-0"
                aria-label={t.language.label}
              >
                <LanguagesIcon className="h-4 w-4 mr-1.5 text-gray-500 dark:text-gray-400 shrink-0" />
                <SelectValue placeholder={t.language.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">{t.language.pt}</SelectItem>
                <SelectItem value="en">{t.language.en}</SelectItem>
                <SelectItem value="es">{t.language.es}</SelectItem>
              </SelectContent>
            </Select>

            <span className="shrink-0">
              <BusinessHoursBadge />
            </span>
            {isAuthenticated && user && (
              <span className="shrink-0">
                <NotificationBell />
              </span>
            )}
            
            {isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hidden sm:flex items-center gap-3 px-3 py-2 hover:bg-blue-50/50 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 min-w-0 max-w-[220px]">
                    <Avatar className="h-9 w-9 ring-2 ring-blue-100 dark:ring-gray-700 flex-shrink-0">
                      <AvatarImage src={avatarUrl ? (avatarUrl.startsWith("http") ? avatarUrl : getApiEndpoint(avatarUrl)) : undefined} alt="" />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold shadow-md">
                        {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start min-w-0 flex-1 overflow-hidden">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate w-full text-left">{user.name || user.email}</span>
                      {showTrialLabel && (
                        <span className="text-[11px] text-amber-700 shrink-0">{trialLabel}</span>
                      )}
                      {user.role && (
                        <span className="text-xs text-gray-500 truncate w-full text-left">
                          {user.role === "admin" ? t.roles.admin : user.role === "internal" ? t.roles.internal : user.role === "analyst" ? t.roles.analyst : user.role === "external" ? t.roles.external : user.role}
                        </span>
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[280px] w-72 shadow-xl border border-gray-200/50 dark:border-gray-700/50">
                  <DropdownMenuLabel className="pb-3">
                    <div className="flex items-center gap-3 pb-2">
                      <Avatar className="h-10 w-10 ring-2 ring-blue-100 dark:ring-gray-700 flex-shrink-0">
                        <AvatarImage src={avatarUrl ? (avatarUrl.startsWith("http") ? avatarUrl : getApiEndpoint(avatarUrl)) : undefined} alt="" />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold">
                          {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col space-y-0.5 min-w-0 flex-1 overflow-hidden">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{user.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{user.email}</p>
                        {showTrialLabel && (
                          <span className="text-[11px] text-amber-700">{trialLabel}</span>
                        )}
                        {user.role && (
                          <span className="text-xs font-medium text-blue-600 capitalize bg-blue-50 px-2 py-0.5 rounded-full w-fit mt-1">
                            {user.role === "admin" ? t.roles.admin : user.role === "internal" ? t.roles.internal : user.role === "analyst" ? t.roles.analyst : user.role === "external" ? t.roles.external : user.role}
                          </span>
                        )}
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.role === "internal" || user.role === "analyst" || user.role === "admin" ? (
                    <DropdownMenuItem asChild>
                      <Link to="/atendimento" className="cursor-pointer hover:bg-blue-50">
                        {t.user.servicePanel}
                      </Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild>
                      <Link to="/chamados" className="cursor-pointer hover:bg-blue-50">
                        {t.user.myTickets}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer hover:bg-blue-50">
                          {t.user.adminDashboard}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/relatorios" className="cursor-pointer hover:bg-blue-50">
                          {t.nav.reports}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/catalogo" className="cursor-pointer hover:bg-blue-50">
                      {t.user.serviceCatalog}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/editar-perfil" className="cursor-pointer hover:bg-blue-50">
                      {t.user.editProfile}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="text-red-600 hover:bg-red-50 cursor-pointer focus:bg-red-50"
                  >
                    <LogOutIcon className="h-4 w-4 mr-2" />
                    {t.common.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button
                  size="sm"
                  className="hidden sm:inline-flex text-white shadow-md hover:shadow-lg transition-all duration-200"
                  style={{ background: `linear-gradient(90deg, ${brandPrimary}, ${brandSecondary})` }}
                >
                  <UserIcon className="h-4 w-4 mr-2" />
                  {t.common.login}
                </Button>
              </Link>
            )}

            {/* Menu lateral: navegacao completa, em qualquer largura de tela */}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg gap-2"
                  aria-label={t.nav.menuTitle}
                >
                  <MenuIcon className="h-5 w-5" />
                  <span className="hidden lg:inline text-sm font-medium">{t.nav.menu}</span>
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[300px] sm:w-[340px] overflow-y-auto">
                <SheetHeader className="text-left">
                  <SheetTitle>{t.nav.menuTitle}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col space-y-2 mt-6">
                  {/* Idioma no menu lateral */}
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 pb-4">
                    <LanguagesIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">{t.language.label}:</span>
                    <Select value={locale} onValueChange={(v) => setLocale(v as Locale)}>
                      <SelectTrigger className="flex-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pt">{t.language.pt}</SelectItem>
                        <SelectItem value="en">{t.language.en}</SelectItem>
                        <SelectItem value="es">{t.language.es}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {navGroups.map((group) => (
                    <div key={group.title} className="pt-2">
                      <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        {group.title}
                      </p>
                      {group.items.map((item) => {
                        const active = isActiveRoute(item.href);
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setIsMenuOpen(false)}
                            aria-current={active ? "page" : undefined}
                            className={`block border-l-2 px-4 py-2.5 text-base font-medium rounded-r-lg transition-all duration-200 ${
                              active
                                ? "border-transparent bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                : "border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                            }`}
                            style={active ? { borderLeftColor: brandPrimary } : undefined}
                          >
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                  {isAuthenticated && user ? (
                    <div className="pt-6 mt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                      <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-gray-800 rounded-lg">
                        <Avatar className="h-10 w-10 ring-2 ring-blue-100 dark:ring-gray-700">
                          <AvatarImage src={avatarUrl ? (avatarUrl.startsWith("http") ? avatarUrl : getApiEndpoint(avatarUrl)) : undefined} alt="" />
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold">
                            {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{user.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                          {showTrialLabel && (
                            <p className="text-[11px] text-amber-700">{trialLabel}</p>
                          )}
                        </div>
                      </div>
                      <Link to="/editar-perfil" className="block" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="outline" className="w-full">
                          {t.user.editProfile}
                        </Button>
                      </Link>
                      <Button
                        onClick={() => { setIsMenuOpen(false); handleLogout(); }}
                        variant="outline" 
                        className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 dark:border-red-800 dark:hover:bg-red-950/30"
                      >
                        <LogOutIcon className="h-4 w-4 mr-2" />
                        {t.common.logout}
                      </Button>
                    </div>
                  ) : (
                    <Link to="/login" className="block pt-6 mt-4 border-t border-gray-200 dark:border-gray-700" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white">
                        <UserIcon className="h-4 w-4 mr-2" />
                        {t.common.login}
                      </Button>
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
          </div>
        </div>
      </div>
      {isAuthenticated && user && showTrialLabel && (
        <div className="bg-amber-50/90 border-b border-amber-100">
          <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 py-2">
            <Alert className="border-amber-200 bg-amber-50/60 text-amber-900">
              <AlertTitle className="text-amber-900">
                {isTrialActive ? t.trial.evaluation : t.trial.ended}
              </AlertTitle>
              <AlertDescription className="text-amber-800">
                {isTrialActive
                  ? t.trial.daysLeft(trialDaysRemaining)
                  : t.trial.contact}
              </AlertDescription>
            </Alert>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
